import { rainApi } from "@/services/rainApi";

// A signal that is never aborted — used to opt out of the global
// AbortController that AsyncStateStore attaches via Api.currentSignal.
const neverAbort = new AbortController().signal;

export interface RadarImageInfo {
	id: string;
	timestamp: string;
	url: string;
	minLon: number;
	minLat: number;
	maxLon: number;
	maxLat: number;
}

export interface LatestImagesResponse {
	RATE: RadarImageInfo;
	ACRR: RadarImageInfo;
	DBZH: RadarImageInfo;
}

export interface RainGaugeStation {
	id: string;
	name: string;
	lon: number;
	lat: number;
	elevation?: number;
	provider: string;
	distanceKm?: number;
}

export interface AllStationsResponse {
	stations: RainGaugeStation[];
}

export interface NearbyStationsResponse {
	stations: RainGaugeStation[];
	searchRadiusKm: number;
}

export interface NearbyStationsParams {
	lon: number;
	lat: number;
	radiusKm?: number;
	limit?: number;
}

export interface RainObservation {
	ts: string;
	value: number;
	source: "rain_gauge" | "radar" | "zero_fill";
}

export interface DataSourceInfo {
	source: "rain_gauge" | "radar";
	station: {
		id: string;
		name: string;
		lon: number;
		lat: number;
		distanceKm: number;
		provider: string;
	} | null;
	fallbackUsed: boolean;
	searchRadiusKm: number;
}

export interface MergeInfo {
	strategy: "gauge_only" | "merged" | "radar_only";
	gaugeCoverage: number;
	gaugeObservations: number;
	radarFillObservations: number;
	overlapQuality: {
		overlapCount: number;
		correlation: number | null;
		meanAbsError: number;
		bias: number;
	};
}

export interface TimeseriesResponse {
	lon: number;
	lat: number;
	start: string;
	end: string;
	unit: string;
	sourceInfo: DataSourceInfo;
	data: RainObservation[];
	mergeInfo?: MergeInfo;
}

export interface TimeseriesParams {
	lon: number;
	lat: number;
	start: string;
	end: string;
	samples?: number;
	forceSource?: "rain_gauge" | "radar";
	maxStationDistanceKm?: number;
	gapFill?: boolean;
}

export interface StationTimeseriesParams {
	start: string;
	end: string;
	gapFill?: boolean;
	samples?: number;
}

export interface RainAggregates {
	avg: number;
	max: number;
	min: number;
	count: number;
	totalMm: number;
}

export interface RollingWindow {
	ts: string;
	value: number;
}

export interface StationStatisticsParams {
	start: string;
	end: string;
	rollingWindowMinutes?: number;
	rollingAggregation?: "avg" | "max" | "min" | "sum";
}

export interface StatisticsParams {
	lon: number;
	lat: number;
	start: string;
	end: string;
	maxStationDistanceKm?: number;
	forceSource?: "rain_gauge" | "radar";
	rollingWindowMinutes?: number;
	rollingAggregation?: "avg" | "max" | "min" | "sum";
}

export interface StationStatisticsResponse {
	lon: number;
	lat: number;
	start: string;
	end: string;
	unit: string;
	sourceInfo: DataSourceInfo;
	aggregates: RainAggregates;
	rollingWindows?: RollingWindow[];
	mergeInfo?: MergeInfo;
}

export interface TimestepsParams {
	productType?: string;
	limit?: number;
}

export interface TimestepsResponse {
	timesteps: string[];
	productType: string;
	total: number;
	limit: number;
	offset: number;
}

export interface PaginatedImages {
	data: RadarImageInfo[];
	total: number;
	limit: number;
	offset: number;
}

export interface RadarImagesParams {
	productType?: string;
	at?: string;
	start?: string;
	end?: string;
	limit?: number;
	offset?: number;
}

export interface ImageUrlResponse {
	url: string;
}

export const rain = {
	getAllStations: async (): Promise<AllStationsResponse> => {
		return rainApi.get<AllStationsResponse>("/rain/stations");
	},

	getNearbyStations: async (params: NearbyStationsParams): Promise<NearbyStationsResponse> => {
		return rainApi.get<NearbyStationsResponse>("/rain/stations/nearby", { params });
	},

	getLatestImages: async (): Promise<LatestImagesResponse> => {
		return rainApi.get<LatestImagesResponse>("/images/latest");
	},

	getTimeseries: async (params: TimeseriesParams): Promise<TimeseriesResponse> => {
		return rainApi.get<TimeseriesResponse>("/rain/timeseries", { params });
	},

	getStationTimeseries: async (stationId: string, params: StationTimeseriesParams): Promise<TimeseriesResponse> => {
		return rainApi.get<TimeseriesResponse>(`/rain/stations/${stationId}/timeseries`, { params });
	},

	getStatistics: async (params: StatisticsParams): Promise<StationStatisticsResponse> => {
		return rainApi.get<StationStatisticsResponse>("/rain/statistics", { params });
	},

	getStationStatistics: async (
		stationId: string,
		params: StationStatisticsParams,
	): Promise<StationStatisticsResponse> => {
		return rainApi.get<StationStatisticsResponse>(`/rain/stations/${stationId}/statistics`, { params });
	},

	getTimesteps: async (params: TimestepsParams = {}): Promise<TimestepsResponse> => {
		return rainApi.get<TimestepsResponse>("/timesteps", { params, signal: neverAbort });
	},

	getImages: async (params: RadarImagesParams = {}): Promise<PaginatedImages> => {
		return rainApi.get<PaginatedImages>("/images", { params, signal: neverAbort });
	},

	getImageUrl: async (id: string): Promise<ImageUrlResponse> => {
		return rainApi.get<ImageUrlResponse>(`/images/${id}/url`, { signal: neverAbort });
	},
};
